import { useEffect, useState } from "react";
import type { ActionState, BuilderApi, Workspace } from "../api/contract";
import { toBuilderError, type BuilderFailure } from "../api/errors";
import { isInFlight, isPublication, isTerminal } from "../lib/workspaceState";

export interface UseActionFollowerOptions {
  api: BuilderApi;
  siteId: string;
  workspace: Workspace | undefined;
  refreshWorkspace: () => Promise<Workspace | undefined>;
  /** Poll delays in ms; the last value repeats. */
  pollDelays?: number[];
  /** Most requests per poll. The cursor rotates so every in-flight action is reached in turn. */
  batchSize?: number;
}

const DEFAULT_POLL_DELAYS = [1000, 2000, 3000, 5000];
const MAX_BACKOFF = 15_000;

/**
 * Follows queued/running edits (not publications, which usePublication owns) through the action
 * endpoint. That read also repairs interrupted runtime handoffs. When any followed action changes
 * state the workspace is refreshed, so activity updates without a reload.
 */
export function useActionFollower({
  api, siteId, workspace, refreshWorkspace, pollDelays = DEFAULT_POLL_DELAYS, batchSize = 3,
}: UseActionFollowerOptions): { failure: BuilderFailure | null } {
  const [failure, setFailure] = useState<BuilderFailure | null>(null);

  const followed = (workspace?.actions ?? [])
    .filter((action) => !isPublication(action) && isInFlight(action));
  // Stable key: restart only when the set of followed actions or their known states change.
  const key = followed.map((action) => `${action.id}:${action.state}`).join("|");

  useEffect(() => {
    if (!key) return;
    const known = new Map<string, ActionState>(key.split("|").map((entry) => {
      const split = entry.lastIndexOf(":");
      return [entry.slice(0, split), entry.slice(split + 1) as ActionState];
    }));
    const controller = new AbortController();
    let attempt = 0;
    let failures = 0;
    let cursor = 0;
    let timer: ReturnType<typeof setTimeout>;

    const schedule = () => {
      const delay = failures > 0
        ? Math.min(MAX_BACKOFF, 2000 * 2 ** (failures - 1))
        : pollDelays[Math.min(attempt, pollDelays.length - 1)]!;
      attempt += 1;
      timer = setTimeout(poll, delay);
    };

    const poll = async () => {
      let changed = false;
      try {
        // Sequential on purpose: at most one request in flight per workspace. Rotate through the
        // whole set so an older action still gets the read that repairs its dispatch.
        const ids = [...known.keys()];
        const batch = Array.from({ length: Math.min(batchSize, ids.length) }, (_, i) => ids[(cursor + i) % ids.length]!);
        cursor = (cursor + batch.length) % Math.max(ids.length, 1);
        for (const id of batch) {
          const state = known.get(id)!;
          const action = await api.getAction(siteId, id, controller.signal);
          if (controller.signal.aborted) return;
          if (action.state !== state) changed = true;
          if (isTerminal(action)) known.delete(id);
          else known.set(id, action.state);
        }
        failures = 0;
      } catch (caught) {
        if (controller.signal.aborted) return;
        const error = toBuilderError(caught);
        if (error.failure === "signed-out" || error.failure === "not-found") {
          setFailure(error.failure);
          return;
        }
        failures += 1;
      }
      // A refresh updates activity; a changed followed set restarts this effect and aborts this loop.
      if (changed) await refreshWorkspace();
      if (!controller.signal.aborted && known.size > 0) schedule();
    };

    schedule();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [api, siteId, key, pollDelays, batchSize, refreshWorkspace]);

  return { failure };
}
